import { styled } from '@mui/material/styles';
import {
  Box,
  DialogTitle,
  IconButton,
  Stepper,
  Step,
  StepButton,
  Typography,
  useMediaQuery,
  useTheme,
  MenuItem,
  Menu,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MoreVert from '@mui/icons-material/MoreVert';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

const PREFIX = 'FullScreenDialogTitle';

const classes = {
  header: `${PREFIX}-header`,
  controls: `${PREFIX}-controls`,
  content: `${PREFIX}-content`,
  heading: `${PREFIX}-heading`,
  aside: `${PREFIX}-aside`,
  title: `${PREFIX}-title`,
  subTitle: `${PREFIX}-subTitle`,
  menu: `${PREFIX}-menu`,
};

interface FullScreenDialogTitleProps {
  title: string;
  subTitle?: string;
  close: () => void;
  children?: React.ReactNode;
  showClose?: boolean;
  showMenu?: boolean;
  handleDownload?: () => void;
  handleShare?: () => void;
}

const Root = styled(DialogTitle)(({ theme }) => ({
  [`&.${classes.header}`]: {
    backgroundColor: 'rgba(1, 16, 18, 0.04)',
    paddingLeft: theme.spacing(18),
    paddingRight: theme.spacing(18),
    paddingBottom: theme.spacing(4.625),
    paddingTop: theme.spacing(7.625),
    position: 'relative',
    boxShadow: 'none',
  },

  [`& .${classes.controls}`]: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    position: 'absolute',
    top: theme.spacing(1.5),
    right: theme.spacing(1.5),
  },

  [`& .${classes.content}`]: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    [theme.breakpoints.up('md')]: {
      marginRight: 144,
    },
    [theme.breakpoints.down('md')]: {
      marginRight: 32,
    },
    marginTop: -12,
    fontFamily: 'Bitter',
  },

  [`& .${classes.title}`]: {
    fontFamily: 'Bitter',
    fontSize: '36px',
    fontStyle: 'normal',
    fontWeight: '700',
    lineHeight: '48px',
    letterSpacing: '0.5px',
    color: 'rgba(1, 16, 18, 0.90)',
  },

  [`& .${classes.subTitle}`]: {
    textTransform: 'uppercase',
    fontSize: '16px',
    fontWeight: '600',
  },

  [`& .${classes.menu}`]: {
    paddingTop: theme.spacing(2.875),
    marginRight: theme.spacing(-18),
  },
}));

const StyledMenu = styled(Menu)(() => ({
  [`& .MuiPaper-root`]: {
    width: '160px',
    marginLeft: '10px',
    borderRadius: '3px',
    boxShadow:
      '0px 5px 5px -3px rgba(0, 0, 0, 0.10), 0px 8px 10px 1px rgba(0, 0, 0, 0.07), 0px 3px 14px 2px rgba(0, 0, 0, 0.06)',

    [`& .MuiList-root`]: {
      padding: 0,
    },
    [`& .MuiMenuItem-root`]: {
      color: 'rgba(1, 16, 18, 0.60)',
      fontFamily: 'Source Sans Pro',
      fontSize: '14px',
      fontStyle: 'normal',
      fontWeight: '400',
      lineHeight: '143%',
      letterSpacing: '0.17px',
      padding: '8px 16px',
    },
  },
}));

const FullScreenDialogTitle = ({
  title,
  subTitle,
  close,
  showClose = true,
  children,
  showMenu = false,
  handleDownload,
}: FullScreenDialogTitleProps) => {
  const handleClose = () => {
    close();
  };
  const { t } = useTranslation('common');
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const openMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const closeMenu = () => {
    setAnchorEl(null);
  };

  const handlePDFDownload = () => {
    if (handleDownload) {
      handleDownload();
    }
    closeMenu();
  };

  const handleShare = () => {
    //TODO: Implement share
    closeMenu();
  };

  return (
    <Root className={classes.header}>
      <Box className={classes.controls}>
        {showClose && (
          <IconButton
            onClick={handleClose}
            aria-label="close"
            data-testid="close"
            size="large">
            <CloseIcon />
          </IconButton>
        )}
      </Box>
      <Box className={classes.content}>
        <Box className={classes.heading}>
          {subTitle && (
            <Typography variant="h6" className={classes.subTitle}>
              {subTitle}
            </Typography>
          )}
          <Typography variant="h4" className={classes.title}>
            {title}
          </Typography>
        </Box>
        {showMenu && (
          <Box className={classes.menu}>
            <IconButton onClick={(e) => openMenu(e)}>
              <MoreVert />
            </IconButton>
            <StyledMenu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              onClose={closeMenu}
              elevation={1}>
              <MenuItem
                onClick={() => {
                  handlePDFDownload();
                }}>
                {t('invoices.download')}
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleShare();
                }}>
                {t('invoices.share')}
              </MenuItem>
            </StyledMenu>
          </Box>
        )}
      </Box>
      <Box className={classes.aside}>{children}</Box>
    </Root>
  );
};

export { FullScreenDialogTitle };
